import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listNotifications, getUnreadCount, markRead, markAllRead } from '../../services/notificationsService';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import { ROUTES } from '../../routes';
import './NotificationsDropdown.css';

/** Where tapping a notification should go, based on its event type. */
function routeForNotification(item) {
  const event = String(item?.event || '');
  if (item.productId && event === 'product_review') return ROUTES.PRODUCT(item.productId);
  if (item.orderId && (event.startsWith('order_') || event.startsWith('delivery_'))) {
    return ROUTES.ORDER(item.orderId);
  }
  return null;
}

const formatDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    const now = new Date();
    if (dt.toDateString() === now.toDateString()) {
      return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return dt.toLocaleDateString('en-GB');
  } catch {
    return '';
  }
};

const NotificationsDropdown = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadCount = useCallback(async () => {
    try {
      const res = await getUnreadCount();
      setUnreadCount(res.unreadCount || 0);
    } catch {
      // Quietly retry on the next poll.
    }
  }, []);

  useAutoRefresh(loadCount, { intervalMs: 60000 });

  const openPanel = async () => {
    setOpen((o) => !o);
    if (!open) {
      try {
        const res = await listNotifications();
        setItems(res.notifications || []);
        setUnreadCount(res.unreadCount || 0);
      } catch {
        setItems([]);
      }
    }
  };

  const handleItemClick = async (item) => {
    if (!item.isRead) {
      try {
        await markRead(item.id);
        setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n)));
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        // Non-fatal — the item just stays marked unread until the next open.
      }
    }

    const to = routeForNotification(item);
    if (to) {
      setOpen(false);
      navigate(to);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead();
      setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // Non-fatal.
    }
  };

  return (
    <div className="notif-dropdown-wrap">
      <button className="notif-dropdown-btn" onClick={openPanel} aria-label={t('common.notifications')}>
        <img className="iconlybulknotification" alt="" src="/icons/Notification.svg" />
        {unreadCount > 0 && <span className="notif-dropdown-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>

      {open && (
        <>
          <div className="notif-dropdown-overlay" onClick={() => setOpen(false)} />
          <div className="notif-dropdown-panel">
            <div className="notif-dropdown-panel-head">
              <span>{t('common.notifications')}</span>
              {unreadCount > 0 && (
                <button className="notif-dropdown-markall" onClick={handleMarkAllRead}>
                  {t('common.markAllRead')}
                </button>
              )}
            </div>
            {items.length === 0 ? (
              <div className="notif-dropdown-empty">{t('common.noNotificationsYet')}</div>
            ) : (
              <div className="notif-dropdown-list">
                {items.map((item) => (
                  <button
                    key={item.id}
                    className={`notif-dropdown-item${item.isRead ? '' : ' notif-dropdown-item-unread'}`}
                    onClick={() => handleItemClick(item)}
                  >
                    <span className="notif-dropdown-item-title">{item.title}</span>
                    {item.body && <span className="notif-dropdown-item-body">{item.body}</span>}
                    <span className="notif-dropdown-item-date">{formatDate(item.createdAt)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsDropdown;
